import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import EventList from 'modules/event-list/index.js';
import { useEventGroup } from 'modules/common/hooks';
import { useAwaitingClientsRegistry, useAwaitingClientsLoadDone } from 'modules/performance/hooks';
import EventListSkeleton from 'modules/event-list/skeletons/event-list-skeleton/index.js';
import EventListUtils from 'modules/event-list/utils.js';
import InPlayUtils from 'modules/inplay/utils.js';
import { isFunction } from 'mojito/utils';
const {
    selectors: { selectFavoriteEvents },
} = MojitoServices.Favorites;

const AnalyticsContextExtender = MojitoCore.Presentation.AnalyticsContext.ContextExtender;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;
const {
    selectEventGroupMarketOptions,
    selectEventGroupMarketOptionsBySportId,
    selectEventGroupState,
} = MojitoServices.EventGroups.selectors;
const { makeSelectEventsState } = MojitoServices.SportsContent.Events.selectors;
const { CONTENT_STATE } = MojitoServices.Common.types;
const ServicesUtils = MojitoServices.Common.utils;
const { UNAVAILABLE } = CONTENT_STATE;
const { LoaderSuspense, FlexPane } = MojitoPresentation.Components;
const { useComponentVisibility } = MojitoPresentation.Hooks;

export default function EventsCoupon(props) {
    const {
        sportId,
        eventGroupsId,
        analyticsName,
        mojitoTools: { config, instanceId },
        onDataLoad,
        enableSorting,
        isInPlayStreaming,
        isFavorites,
        isOutrights,
        shouldRequestData,
    } = props;

    const [visible, setVisible] = useState(!config.enableContentLazyLoading);
    const allEventGroups = useEventGroup(visible ? eventGroupsId : undefined, shouldRequestData);
    const favoriteEvents = useSelector(selectFavoriteEvents);
    const marketOptions = useSelector(state =>
        isInPlayStreaming
            ? selectEventGroupMarketOptionsBySportId(eventGroupsId, sportId, state)
            : selectEventGroupMarketOptions(eventGroupsId, state)
    );

    const { maxGroupsToRender, maxEventsToRender, initialExpandedGroups, reportLoadingStatus } =
        config;

    const eventGroups = useMemo(() => {
        if (isFavorites) {
            return InPlayUtils.filterEventGroupsByFavoriteEvents(allEventGroups, favoriteEvents);
        }

        const groups = EventListUtils.resolveViewableGroups(
            maxGroupsToRender,
            maxEventsToRender,
            allEventGroups
        );

        if (!isInPlayStreaming) {
            return groups;
        }

        return EventListUtils.resolveGroupsPerSport(sportId, groups);
    }, [
        allEventGroups,
        maxGroupsToRender,
        maxEventsToRender,
        isInPlayStreaming,
        sportId,
        isFavorites,
        favoriteEvents,
    ]);

    const analyticsContext = useMemo(
        () => new AnalyticsContextBuilder().withAnalyticsPath(analyticsName).build(),
        [analyticsName]
    );
    const initialExpandedGroupIds = useMemo(
        () => EventListUtils.resolveInitialExpandedGroups(initialExpandedGroups, eventGroups),
        [initialExpandedGroups, eventGroups]
    );
    const { loadDone, groupsLoadingState } = useGroupsLoadingDone(
        eventGroupsId,
        eventGroups,
        initialExpandedGroups
    );

    const reportingInstanceId = reportLoadingStatus && visible ? instanceId : undefined;
    const syncAwaitingClients = useAwaitingClientsRegistry(
        'EventsCoupon',
        loadDone,
        reportingInstanceId
    );
    const onShowElement = useCallback(() => {
        setVisible(true);
        syncAwaitingClients();
    }, [syncAwaitingClients]);
    const elementRef = useComponentVisibility(onShowElement);
    const noContent = loadDone && eventGroups?.length === 0;
    useAwaitingClientsLoadDone(loadDone, reportingInstanceId, noContent, 'EventsCoupon');

    const contentVersion = useContentVersion(eventGroupsId);

    // Determining children modules loading status
    const setLoadedEventGroups = useDataLoaded(onDataLoad, eventGroupsId, eventGroups);
    const onChildrenLoadDone = useCallback(
        eventGroups => setLoadedEventGroups(eventGroups),
        [setLoadedEventGroups]
    );

    if (groupsLoadingState === UNAVAILABLE || eventGroups?.length === 0) {
        return null;
    }

    const isSortingEnabled = config.enableSorting && enableSorting;

    const skeleton = (
        <EventListSkeleton
            config={config.eventListSkeleton}
            sportId={sportId}
            enableSorting={isSortingEnabled}
            isInPlay={props.isInPlay}
            eventListConfig={config.eventList}
            isOutrights={isOutrights}
        />
    );

    const headerText = isFunction(props.headerText)
        ? props.headerText(eventGroups)
        : props.headerText;

    return (
        <FlexPane config={config.container} elementRef={elementRef} class={'ta-EventsCoupon'}>
            <LoaderSuspense
                config={config.skeletonLoader}
                isContentPending={!loadDone && visible}
                contentHash={contentVersion}
                loader={skeleton}
            >
                {eventGroups && (
                    <AnalyticsContextExtender value={analyticsContext}>
                        <EventList
                            headerText={headerText}
                            config={config.eventList}
                            sportId={sportId}
                            marketOptions={marketOptions}
                            eventGroups={eventGroups}
                            initialExpandedGroupIds={initialExpandedGroupIds}
                            onShowAllButtonClick={props.onShowAllButtonClick}
                            isInPlay={props.isInPlay}
                            isLiveAndFuture={props.isLiveAndFuture}
                            onDataLoad={onChildrenLoadDone}
                            enableSorting={isSortingEnabled}
                        />
                    </AnalyticsContextExtender>
                )}
            </LoaderSuspense>
        </FlexPane>
    );
}

const useDataLoaded = (onDataLoaded, eventGroupsId, eventGroups = []) => {
    const [loadedEventGroups, setLoadedEventGroups] = useState([]);
    const loadedGroupIds = useMemo(
        () => loadedEventGroups.map(({ id }) => id),
        [loadedEventGroups]
    );
    const allGroupIds = useMemo(() => eventGroups.map(({ id }) => id), [eventGroups]);
    useEffect(() => {
        const hasIds = !!allGroupIds.length;
        if (
            allGroupIds.length === loadedEventGroups.length &&
            hasIds &&
            allGroupIds.every(id => loadedGroupIds.includes(id))
        ) {
            onDataLoaded(eventGroupsId);
        }
    }, [loadedGroupIds, allGroupIds, onDataLoaded, eventGroupsId, loadedEventGroups.length]);
    return setLoadedEventGroups;
};

const useGroupsLoadingDone = (eventGroupsId, eventGroups, initialExpandedGroups) => {
    const groupsLoadingState = useSelector(state => selectEventGroupState(eventGroupsId, state));
    const eventIds = useMemo(() => {
        const expandedGroupIds = EventListUtils.resolveInitialExpandedGroups(
            initialExpandedGroups,
            eventGroups
        );
        return (eventGroups || [])
            .filter(group => expandedGroupIds.includes(group.id))
            .flatMap(group => group.events)
            .map(event => event.id);
    }, [initialExpandedGroups, eventGroups]);

    const selectEventsState = useMemo(makeSelectEventsState, []);
    const eventsLoadDone = useSelector(state => {
        const loadingValues = Object.values(selectEventsState(eventIds, state));
        const everyEventRequested = loadingValues.length === eventIds.length;
        return everyEventRequested && loadingValues.every(ServicesUtils.isContentLoadDone);
    });
    // Both AVAILABLE or UNAVAILABLE state can appear on group and on events after it has been requested, and we received the response.
    const groupLoadDone = ServicesUtils.isContentLoadDone(groupsLoadingState);
    const loadDone = groupLoadDone && eventsLoadDone;
    return { loadDone, groupsLoadingState };
};

const useContentVersion = eventGroupsId => {
    const groupIdRef = useRef(eventGroupsId);
    const contentVersionRef = useRef(0);
    if (eventGroupsId !== groupIdRef.current) {
        contentVersionRef.current = Math.random();
        groupIdRef.current = eventGroupsId;
    }
    return `${eventGroupsId}-${contentVersionRef.current}`;
};
